import { useState } from "react";
import { Loader2, RefreshCw, RotateCcw, RotateCw } from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { Input } from "./ui/input";

const ORIENTS = [["auto", "Auto"], ["verticale", "Verticale"], ["orizzontale", "Orizzontale"]];

export const areaLabel = (f, i) => `Area ${i + 1}${f?.nome ? ` · ${f.nome}` : ""}`;

// Elenco aree (chip) con conteggio moduli/kWp: clic = seleziona l'area da modificare.
export function AreaChips({ falde, panels, wp, sel, onSel }) {
  const inc = falde.filter((f) => (f.copertura || "inclinato") !== "piano");
  if (inc.length < 2) return null;
  const tot = panels.length;
  return (
    <div className="flex flex-wrap items-center gap-1.5 mb-2" data-testid="area-chips">
      {inc.map((f) => {
        const i = falde.indexOf(f), n = panels.filter((p) => p.falda_id === f.id).length, on = sel === f.id;
        return (
          <button key={f.id} type="button" onClick={() => onSel(on ? null : f.id)} data-testid={`area-chip-${i + 1}`}
            className={`text-xs rounded-full border px-3 py-1 transition-colors ${on ? "bg-primary text-primary-foreground border-primary" : "bg-background hover:bg-muted border-border"}`}>
            <span className="font-semibold">{areaLabel(f, i)}</span> · {n} mod · {((n * wp) / 1000).toFixed(2)} kWp
          </button>
        );
      })}
      <span className="text-xs text-muted-foreground ml-1" data-testid="area-total">Totale {tot} mod · {((tot * wp) / 1000).toFixed(2)} kWp</span>
    </div>
  );
}

// Impostazioni della singola area + "Rigenera solo quest'area" (i pannelli tornano all'editor, poi si salva).
export function AreaSettingsPanel({ falda, index, base, onPanels, onClose }) {
  const c = falda.layout_cfg || {};
  const [cfg, setCfg] = useState({ nome: falda.nome || "", orientation: c.orientation || "auto", rot_deg: Number(c.rot_deg) || 0, align: c.align || "massimo",
    border_m: c.border_m ?? "", gap_m: c.gap_m ?? "", excluded: !!c.excluded });
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setCfg((s) => ({ ...s, [k]: v }));
  const run = async (regenerate) => {
    setBusy(true);
    try {
      const body = { falda_id: falda.id, nome: cfg.nome, orientation: cfg.orientation, rot_deg: Number(cfg.rot_deg) || 0, align: cfg.align,
        border_m: cfg.border_m === "" ? null : Number(cfg.border_m), gap_m: cfg.gap_m === "" ? null : Number(cfg.gap_m), excluded: cfg.excluded, regenerate };
      const { data } = await api.post(`${base}/panel-layout/area`, body);
      falda.layout_cfg = data.layout_cfg; falda.nome = cfg.nome;
      if (regenerate) { onPanels(falda.id, data.panels || []); toast.success(`${areaLabel(falda, index)}: ${(data.panels || []).length} moduli. Ricorda di salvare.`); }
      else toast.success("Impostazioni area salvate");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };
  return (
    <div className="rounded-lg border border-primary/40 bg-muted/40 p-3 mb-2 flex flex-wrap items-end gap-3" data-testid="area-settings">
      <div className="text-sm font-semibold w-full flex items-center justify-between">
        <span>Impostazioni {areaLabel(falda, index)}</span>
        <Button size="sm" variant="ghost" onClick={onClose} data-testid="area-settings-close">Chiudi</Button>
      </div>
      <label className="text-xs">Nome<Input value={cfg.nome} onChange={(e) => set("nome", e.target.value)} placeholder="es. Capannone Est" className="h-8 w-40" data-testid="area-name-input" /></label>
      <div className="text-xs">Orientamento
        <div className="flex gap-1 mt-0.5">
          {ORIENTS.map(([k, l]) => (
            <Button key={k} size="sm" variant={cfg.orientation === k ? "default" : "outline"} className="h-8" onClick={() => set("orientation", k)} data-testid={`area-orient-${k}`}>{l}</Button>
          ))}
        </div>
      </div>
      <div className="text-xs">Allinea a
        <div className="flex gap-1 mt-0.5">
          {[["massimo", "Massimo riempimento"], ["canalina", "Canalina"], ["colmo", "Colmo"]].map(([k, l]) => (
            <Button key={k} size="sm" variant={cfg.align === k ? "default" : "outline"} className="h-8" onClick={() => set("align", k)} data-testid={`area-align-${k}`}>{l}</Button>
          ))}
        </div>
      </div>
      <div className="text-xs">Rotazione griglia
        <div className="flex items-center gap-1 mt-0.5">
          <Button size="icon" variant="outline" className="h-8 w-8" onClick={() => set("rot_deg", Math.max(-90, cfg.rot_deg - 1))} data-testid="area-rot-minus"><RotateCcw className="h-3.5 w-3.5" /></Button>
          <Input type="number" value={cfg.rot_deg} onChange={(e) => set("rot_deg", e.target.value)} className="h-8 w-16" data-testid="area-rot-input" />
          <Button size="icon" variant="outline" className="h-8 w-8" onClick={() => set("rot_deg", Math.min(90, Number(cfg.rot_deg) + 1))} data-testid="area-rot-plus"><RotateCw className="h-3.5 w-3.5" /></Button>
          <span className="text-muted-foreground">°</span>
        </div>
      </div>
      <label className="text-xs">Margine bordo (m)<Input type="number" step="0.05" value={cfg.border_m} onChange={(e) => set("border_m", e.target.value)} placeholder="default" className="h-8 w-20" data-testid="area-border-input" /></label>
      <label className="text-xs">Distanza moduli (m)<Input type="number" step="0.005" value={cfg.gap_m} onChange={(e) => set("gap_m", e.target.value)} placeholder="default" className="h-8 w-20" data-testid="area-gap-input" /></label>
      <label className="text-xs flex items-center gap-1.5 h-8"><input type="checkbox" checked={!cfg.excluded} onChange={(e) => set("excluded", !e.target.checked)} data-testid="area-include-checkbox" />Includi area</label>
      <div className="flex gap-2 ml-auto">
        <Button size="sm" variant="outline" onClick={() => run(false)} disabled={busy} data-testid="area-save-settings">Salva impostazioni</Button>
        <Button size="sm" onClick={() => run(true)} disabled={busy} data-testid="area-regenerate">
          {busy ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-1" />}Rigenera solo quest'area
        </Button>
      </div>
      <p className="text-[11px] text-muted-foreground w-full">La rigenerazione sostituisce i moduli di quest'area (le modifiche manuali dell'area si perdono, le altre aree restano invariate). Poi premi Salva.</p>
    </div>
  );
}
